import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { expertService } from '../api/services';
import useApi from '../hooks/useApi';
import { Search, Star, MapPin, Clock, AlertCircle, Filter, X } from 'lucide-react';

const VIDEO_URL = 'https://firebasestorage.googleapis.com/v0/b/images-1aaab.appspot.com/o/20546634-uhd_3840_2160_30fps.mp4?alt=media&token=43e2bb92-2523-416f-a0b3-a1c42a351537';

// ── Expert Card ───────────────────────────────────────────────
const ExpertCard = ({ expert }) => {
  const name = `${expert.user?.firstName ?? ''} ${expert.user?.lastName ?? ''}`.trim();
  const initials = `${expert.user?.firstName?.[0] ?? ''}${expert.user?.lastName?.[0] ?? ''}`;
  const rate = expert.hourlyRate
    ? `${expert.currency === 'USD' ? '$' : 'GH₵'}${expert.hourlyRate}/hr`
    : 'Rate on request';
  const specs = Array.isArray(expert.specializations) ? expert.specializations : [];

  return (
    <div className="group bg-white border border-cream-200 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300">
      <div className="p-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="relative flex-shrink-0">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-forest-600 to-forest-800 text-white flex items-center justify-center font-serif font-semibold text-lg">
              {expert.user?.avatar
                ? <img src={expert.user.avatar} alt={name} className="w-full h-full object-cover rounded-xl" />
                : initials}
            </div>
            {/* Online indicator dot */}
            {expert.isOnline && (
              <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-serif font-semibold text-forest-900 leading-tight">{name}</h3>
              {expert.isOnline && (
                <span className="flex items-center gap-1 text-[10px] font-semibold bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                  Online
                </span>
              )}
            </div>
            <p className="text-xs text-forest-600/70 mt-0.5 truncate">
              {specs.slice(0, 2).join(' · ') || 'Tourism Expert'}
            </p>
            <div className="flex items-center gap-1 mt-1">
              <MapPin size={10} className="text-forest-400" />
              <span className="text-xs text-forest-500">{expert.user?.city ?? expert?.timezone}</span>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="flex items-center gap-1 text-sm font-semibold text-forest-900">
              <span>🎓</span>
              <span>
                {expert?.levelScope === "ALL_LEVELS"
                  ? "All Levels"
                  : expert?.levelScope}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-4">
          {specs.slice(0, 3).map((t) => (
            <span key={t} className="text-[10px] font-medium bg-forest-50 text-forest-700 border border-forest-100 px-2 py-0.5 rounded-full">{t}</span>
          ))}
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-cream-100">
          <div className="flex items-center gap-3 text-xs text-forest-600/60">
            {expert.averageRating > 0 && (
              <span className="flex items-center gap-1">
                <Star size={11} className="fill-gold-400 text-gold-400" />
                {Number(expert.averageRating).toFixed(1)}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock size={11} /> {expert.yearsOfExperience} yrs
            </span>
          </div>
          <Link to={`/experts/${expert.id}`} className="btn-primary text-xs py-1.5 px-4">Book</Link>
        </div>
      </div>
    </div>
  );
};

const SkeletonCard = () => (
  <div className="bg-white border border-cream-200 rounded-2xl p-6 animate-pulse space-y-4">
    <div className="flex gap-4">
      <div className="w-12 h-12 bg-cream-200 rounded-xl flex-shrink-0" />
      <div className="flex-1 space-y-2 pt-1">
        <div className="h-4 bg-cream-200 rounded w-3/4" />
        <div className="h-3 bg-cream-100 rounded w-1/2" />
      </div>
    </div>
    <div className="flex gap-2">
      <div className="h-5 bg-cream-100 rounded-full w-20" />
      <div className="h-5 bg-cream-100 rounded-full w-16" />
    </div>
    <div className="h-px bg-cream-100" />
    <div className="flex justify-between items-center">
      <div className="h-3 bg-cream-100 rounded w-24" />
      <div className="h-7 bg-cream-200 rounded-full w-16" />
    </div>
  </div>
);

// ── Popular categories shown when search is empty ────────────
const POPULAR = [
  { label: 'Heritage Tourism', emoji: '🏛️' },
  { label: 'Eco-Tourism', emoji: '🌿' },
  { label: 'Hospitality', emoji: '🏨' },
  { label: 'Tour Operations', emoji: '🗺️' },
  { label: 'Food Tourism', emoji: '🍽️' },
  { label: 'Adventure Tourism', emoji: '⛵' },
  { label: 'Cultural Events', emoji: '🎭' },
  { label: 'Travel Photography', emoji: '📸' },
];

// ── Search with dropdown suggestions ─────────────────────────
const SearchInput = ({ allExperts, onSearch }) => {
  const [value, setValue] = useState('');
  const [focused, setFocused] = useState(false);
  const [suggestions, setSuggestions] = useState({ specs: [], experts: [] });
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (!ref.current?.contains(e.target)) setFocused(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setValue(v);

    if (v.trim().length < 1) {
      setSuggestions({ specs: [], experts: [] });
      return;
    }

    const q = v.toLowerCase();

    // Collect all unique specializations from experts
    const allSpecs = [...new Set(
      allExperts.flatMap(ex => Array.isArray(ex.specializations) ? ex.specializations : [])
    )];
    const matchedSpecs = allSpecs.filter(s => s.toLowerCase().includes(q)).slice(0, 4);

    // Match expert names
    const matchedExperts = allExperts.filter(ex => {
      const name = `${ex.user?.firstName ?? ''} ${ex.user?.lastName ?? ''}`.toLowerCase();
      return name.includes(q);
    }).slice(0, 3);

    setSuggestions({ specs: matchedSpecs, experts: matchedExperts });
  };

  const pickSpec = (spec) => {
    setValue(spec);
    setFocused(false);
    onSearch(spec);
  };

  const pickExpert = (expert) => {
    const name = `${expert.user?.firstName ?? ''} ${expert.user?.lastName ?? ''}`.trim();
    setValue(name);
    setFocused(false);
    onSearch(name);
  };

  const submit = (e) => {
    e.preventDefault();
    setFocused(false);
    onSearch(value);
  };

  const clear = () => { setValue(''); setSuggestions({ specs: [], experts: [] }); onSearch(''); };

  const showPopular = focused && value.trim().length === 0;
  const showResults = focused && value.trim().length > 0 && (suggestions.specs.length > 0 || suggestions.experts.length > 0);
  const isOpen = showPopular || showResults;

  return (
    <div ref={ref} className="relative flex-1">
      <form onSubmit={submit}>
        <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 z-10 pointer-events-none" />
        <input
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          placeholder="What kind of expert are you looking for?"
          className="w-full pl-10 pr-10 py-3.5 rounded-xl text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-green-400/40"
          style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)' }}
        />
        {value && (
          <button type="button" onClick={clear} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70">
            <X size={14} />
          </button>
        )}
      </form>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 rounded-2xl overflow-hidden shadow-2xl border border-white/10 z-50"
          style={{ background: 'rgba(8,24,12,0.98)', backdropFilter: 'blur(20px)' }}>

          {/* Popular categories when empty */}
          {showPopular && (
            <>
              <div className="px-4 pt-3 pb-2">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-white/30">Popular Categories</p>
              </div>
              <div className="grid grid-cols-2 gap-0">
                {POPULAR.map((cat) => (
                  <button key={cat.label} onClick={() => pickSpec(cat.label)}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/[0.06] transition-colors text-left border-b border-white/[0.04]">
                    <span className="text-base">{cat.emoji}</span>
                    <span className="text-sm text-white/75">{cat.label}</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Search results */}
          {showResults && (
            <>
              {/* Specialization matches */}
              {suggestions.specs.length > 0 && (
                <>
                  <div className="px-4 pt-3 pb-1.5">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-white/30">Specializations</p>
                  </div>
                  {suggestions.specs.map((spec) => (
                    <button key={spec} onClick={() => pickSpec(spec)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/[0.06] transition-colors text-left border-b border-white/[0.04]">
                      <Search size={13} className="text-green-400 flex-shrink-0" />
                      <span className="text-sm text-white/80">{spec}</span>
                    </button>
                  ))}
                </>
              )}

              {/* Expert name matches */}
              {suggestions.experts.length > 0 && (
                <>
                  <div className="px-4 pt-3 pb-1.5">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-white/30">Experts</p>
                  </div>
                  {suggestions.experts.map((expert) => {
                    const name = `${expert.user?.firstName ?? ''} ${expert.user?.lastName ?? ''}`.trim();
                    const initials = `${expert.user?.firstName?.[0] ?? ''}${expert.user?.lastName?.[0] ?? ''}`;
                    const specs = Array.isArray(expert.specializations) ? expert.specializations : [];
                    const rate = expert.hourlyRate
                      ? `${expert.currency === 'USD' ? '$' : 'GH₵'}${expert.hourlyRate}/hr`
                      : null;
                    return (
                      <button key={expert.id} onClick={() => pickExpert(expert)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.06] transition-colors text-left border-b border-white/[0.04] last:border-0">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-forest-600 to-forest-800 text-white flex items-center justify-center font-serif font-semibold text-xs flex-shrink-0 overflow-hidden">
                          {expert.user?.avatar
                            ? <img src={expert.user.avatar} alt={name} className="w-full h-full object-cover" />
                            : initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-white truncate">{name}</p>
                          <p className="text-xs text-white/35 truncate">{specs.slice(0, 2).join(' · ') || 'Tourism Expert'}</p>
                        </div>
                        {rate && <span className="text-xs font-medium text-green-400 flex-shrink-0">{rate}</span>}
                      </button>
                    );
                  })}
                </>
              )}
            </>
          )}

          {/* No results */}
          {focused && value.trim().length > 0 && suggestions.specs.length === 0 && suggestions.experts.length === 0 && (
            <div className="px-4 py-5 text-center">
              <p className="text-sm text-white/40">No matches — press Enter to search anyway</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────
const ExpertsPage = () => {
  const [minRating, setMinRating] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedRating, setAppliedRating] = useState('');
  const [videoReady, setVideoReady] = useState(false);

  const params = { page: 1, limit: 12 };
  if (appliedSearch) params.search = appliedSearch;
  if (appliedRating) params.minRating = appliedRating;

  // Load all experts for autocomplete (no filters)
  const { data: allData } = useApi(() => expertService.browse({ page: 1, limit: 100 }), []);
  const allExperts = Array.isArray(allData) ? allData
    : Array.isArray(allData?.experts) ? allData.experts
      : Array.isArray(allData?.data) ? allData.data : [];

  const { data, loading, error } = useApi(
    () => expertService.browse(params),
    [appliedSearch, appliedRating],
  );

  const experts = Array.isArray(data) ? data
    : Array.isArray(data?.experts) ? data.experts
      : Array.isArray(data?.data) ? data.data : [];
  const total = data?.total ?? data?.meta?.total ?? experts.length;

  const handleSearch = (val) => {
    setAppliedSearch(val);
    setAppliedRating(minRating);
  };

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />

      {/* ── Video header ── */}
      <div className="relative" style={{ minHeight: 380 }}>

        {/* Video */}
        <video
          autoPlay muted loop playsInline
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
          style={{ zIndex: 0, opacity: videoReady ? 1 : 0 }}
          onCanPlay={() => setVideoReady(true)}
        >
          <source src={VIDEO_URL} type="video/mp4" />
        </video>

        {/* Fallback while video loads */}
        {!videoReady && (
          <div className="absolute inset-0" style={{ zIndex: 0, background: '#0a1e0d' }} />
        )}

        {/* Overlay */}
        <div className="absolute inset-0"
          style={{ zIndex: 1, background: 'linear-gradient(160deg, rgba(2,10,5,0.82) 0%, rgba(3,14,7,0.78) 50%, rgba(1,8,3,0.88) 100%)' }} />

        {/* Kente stripe */}
        <div className="absolute top-0 left-0 right-0 h-[3px]"
          style={{ zIndex: 2, background: 'linear-gradient(90deg, #dc2626, #d97706, #16a34a, #d97706, #dc2626)' }} />

        {/* Content */}
        <div className="relative z-10 pt-32 pb-20 px-6" style={{ zIndex: 10 }}>
          <div className="max-w-3xl mx-auto text-center space-y-5">

            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-widest"
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              Browse Experts
            </div>

            <h1 className="font-serif text-4xl lg:text-5xl font-bold text-white leading-tight">
              Find your <span className="italic font-light text-green-300">expert</span>
            </h1>

            <p className="text-white/55 max-w-md mx-auto text-sm leading-relaxed">
              Verified specialists across all industries, ready to guide your business.
            </p>

            {/* Search + filter row */}
            <div className="flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto mt-2">
              <SearchInput allExperts={allExperts} onSearch={handleSearch} />

              <select
                value={minRating}
                onChange={e => setMinRating(e.target.value)}
                className="py-3.5 px-4 rounded-xl text-sm text-white focus:outline-none focus:ring-2 focus:ring-green-400/40 flex-shrink-0"
                style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)' }}
              >
                <option value="" style={{ background: '#0a1e0d' }}>Any Rating</option>
                <option value="3" style={{ background: '#0a1e0d' }}>3★ & above</option>
                <option value="4" style={{ background: '#0a1e0d' }}>4★ & above</option>
                <option value="4.5" style={{ background: '#0a1e0d' }}>4.5★ & above</option>
              </select>

              <button
                onClick={() => { setAppliedSearch(''); setAppliedRating(minRating); handleSearch(''); }}
                className="flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl font-semibold text-white text-sm
                  bg-gradient-to-br from-green-700 to-green-500 hover:scale-[1.02] transition-transform shadow-lg shadow-green-900/30 flex-shrink-0"
              >
                <Filter size={14} /> Filter
              </button>
            </div>
          </div>
        </div>

        {/* Bottom fade into cream */}
        <div className="absolute bottom-0 left-0 right-0 h-16"
          style={{ zIndex: 3, background: 'linear-gradient(to bottom, transparent, #f8f6f0)' }} />
      </div>

      {/* ── Results ── */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <p className="text-sm text-forest-600 mb-6">
          {loading ? 'Searching...' : `${total} expert${total !== 1 ? 's' : ''} found`}
        </p>

        {error && (
          <div className="flex items-center gap-3 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-5 py-4 mb-8">
            <AlertCircle size={16} />
            <span className="text-sm font-medium">Could not load experts — make sure your API server is running.</span>
          </div>
        )}

        {!loading && !error && experts.length === 0 && (
          <div className="text-center py-20 text-forest-500">
            <Search size={40} className="mx-auto mb-4 opacity-30" />
            <p className="font-serif text-xl">No experts found</p>
            <p className="text-sm mt-2">Try adjusting your search or filters</p>
          </div>
        )}

        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
          {loading
            ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
            : [...experts].sort((a, b) => (b.isOnline ? 1 : 0) - (a.isOnline ? 1 : 0)).map((e) => <ExpertCard key={e.id} expert={e} />)}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ExpertsPage;